import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';

const TradingHistory = () => {
  const { tokens } = useAuth();
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // 'all', 'buy', 'sell', 'completed', 'failed'
  const [timeRange, setTimeRange] = useState('7d'); // '24h', '7d', '30d', 'all'

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://safepumback.civicstech.name.ng/api';

  useEffect(() => {
    if (tokens?.access_token) {
      fetchTradingHistory();
    }
  }, [tokens, filter, timeRange]);

  const makeAuthenticatedRequest = async (url, options = {}) => {
    if (!tokens?.access_token) {
      throw new Error('No authentication token available');
    }

    const response = await fetch(`${API_BASE_URL}${url}`, {
      ...options,
      headers: {
        'Authorization': `Bearer ${tokens.access_token}`,
        'Content-Type': 'application/json',
        ...options.headers
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error?.message || `HTTP ${response.status}`);
    }

    return response.json();
  };

  const fetchTradingHistory = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        limit: '50',
        offset: '0'
      });

      if (filter !== 'all') {
        if (['buy', 'sell'].includes(filter)) {
          params.append('trade_type', filter);
        } else if (['completed', 'failed', 'pending'].includes(filter)) {
          params.append('status', filter);
        }
      }

      if (timeRange !== 'all') {
        const now = new Date();
        let startDate;
        
        switch (timeRange) {
          case '24h':
            startDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
            break;
          case '7d':
            startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            break;
          case '30d':
            startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
            break;
          default:
            startDate = null;
        }
        
        if (startDate) {
          params.append('start_date', startDate.toISOString());
        }
      }

      const data = await makeAuthenticatedRequest(`/trading/history?${params}`);
      setTrades(data.data.trades || []);
    } catch (error) {
      setError(error.message);
      console.error('Error fetching trading history:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatAmount = (amount, decimals = 6) => {
    const num = parseFloat(amount) / Math.pow(10, decimals);
    return num.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 6
    });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTokenSymbol = (tokenAddress) => {
    const KNOWN_TOKENS = {
      'So11111111111111111111111111111111111111112': 'SOL',
      'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v': 'USDC',
      'Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB': 'USDT'
    };
    return KNOWN_TOKENS[tokenAddress] || `${tokenAddress.slice(0, 4)}...${tokenAddress.slice(-4)}`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return '#28a745';
      case 'failed': return '#dc3545';
      case 'pending': return '#ffc107';
      default: return '#6c757d';
    }
  };

  const calculateTradingStats = () => {
    const completedTrades = trades.filter(trade => trade.status === 'completed');
    const totalTrades = trades.length;
    const successRate = totalTrades > 0 ? (completedTrades.length / totalTrades * 100).toFixed(1) : 0;
    
    const buyTrades = completedTrades.filter(trade => trade.trade_type === 'buy').length;
    const sellTrades = completedTrades.filter(trade => trade.trade_type === 'sell').length;

    return {
      totalTrades,
      completedTrades: completedTrades.length,
      successRate,
      buyTrades,
      sellTrades
    };
  };

  const stats = calculateTradingStats();

  if (loading) {
    return (
      <div className="trading-history">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading trading history...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="trading-history">
      <div className="history-header">
        <h3>Trading History</h3>
        <div className="history-controls">
          <select 
            value={filter} 
            onChange={(e) => setFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Trades</option>
            <option value="buy">Buy Orders</option>
            <option value="sell">Sell Orders</option>
            <option value="completed">Completed</option>
            <option value="failed">Failed</option>
            <option value="pending">Pending</option>
          </select>
          
          <select 
            value={timeRange} 
            onChange={(e) => setTimeRange(e.target.value)}
            className="time-select"
          >
            <option value="24h">Last 24h</option>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="all">All time</option>
          </select>
        </div>
      </div>

      {/* Trading Statistics */}
      <div className="trading-stats">
        <div className="stat-card">
          <span className="stat-label">Total Trades</span>
          <span className="stat-value">{stats.totalTrades}</span>
        </div>
        <div className="stat-card">
          <span className="stat-label">Success Rate</span>
          <span className="stat-value">{stats.successRate}%</span>
        </div>
        <div className="stat-card">
          <span className="stat-label">Buy Orders</span>
          <span className="stat-value buy">{stats.buyTrades}</span>
        </div>
        <div className="stat-card">
          <span className="stat-label">Sell Orders</span>
          <span className="stat-value sell">{stats.sellTrades}</span>
        </div>
      </div>

      {error && (
        <div className="error-message">
          <span className="error-icon">❌</span>
          {error}
        </div>
      )}

      {trades.length === 0 ? (
        <div className="empty-history">
          <div className="empty-icon">📊</div>
          <h4>No Trading History</h4>
          <p>Your completed trades will appear here</p>
        </div>
      ) : (
        <div className="trades-list">
          {trades.map((trade) => (
            <div key={trade.id} className="trade-item">
              <div className="trade-main">
                <div className="trade-type">
                  <span className={`type-badge ${trade.trade_type}`}>
                    {trade.trade_type.toUpperCase()}
                  </span>
                  <span className="trade-pair">
                    {getTokenSymbol(trade.input_token)} → {getTokenSymbol(trade.output_token)}
                  </span>
                </div>
                
                <div className="trade-amounts">
                  <div className="amount-row">
                    <span className="amount-label">Paid:</span>
                    <span className="amount-value">
                      {formatAmount(trade.input_amount, trade.input_token === 'So11111111111111111111111111111111111111112' ? 9 : 6)} {getTokenSymbol(trade.input_token)}
                    </span>
                  </div>
                  <div className="amount-row">
                    <span className="amount-label">Received:</span>
                    <span className="amount-value">
                      {formatAmount(trade.output_amount, trade.output_token === 'So11111111111111111111111111111111111111112' ? 9 : 6)} {getTokenSymbol(trade.output_token)}
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="trade-meta">
                <div className="trade-status">
                  <span 
                    className="status-indicator"
                    style={{ backgroundColor: getStatusColor(trade.status) }}
                  ></span>
                  <span className="status-text">{trade.status}</span>
                </div>
                
                <div className="trade-time">
                  {formatDate(trade.created_at)}
                </div>
                
                {trade.transaction_hash && (
                  <div className="trade-hash">
                    <a 
                      href={`https://explorer.solana.com/tx/${trade.transaction_hash}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hash-link"
                    >
                      View Transaction
                    </a>
                  </div>
                )}
              </div>
              
              {trade.error_message && (
                <div className="trade-error">
                  <span className="error-label">Error:</span>
                  <span className="error-text">{trade.error_message}</span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TradingHistory;